import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

interface CompletionStepProps {
  onNext: (data?: any) => void;
  onBack: () => void;
  existingData?: any;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const CompletionStep: React.FC<CompletionStepProps> = ({
  onNext,
  onBack,
  isFirstStep = false,
  isLastStep = true,
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const hasAutoSaved = useRef(false);

  // Auto-trigger save when completion step appears (only once)
  useEffect(() => {
    if (!hasAutoSaved.current) {
      hasAutoSaved.current = true;
      const autoSave = async () => {
        setIsSubmitting(true);
        try {
          await onNext();
        } catch (error) {
          console.error('Auto-save error:', error);
          setIsSubmitting(false);
          hasAutoSaved.current = false; // Allow retry on error
        }
      };

      autoSave();
    }
  }, []); // Empty dependency array - only run once on mount

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      await onNext();
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <CheckCircle
          sx={{
            fontSize: 80,
            color: '#4caf50',
            mb: 2,
          }}
        />
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
        Your account is all set!
      </Typography>

      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
        You're ready to start using DriverVerified. Click below to access your dashboard.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={isSubmitting}
          sx={{ minWidth: 120 }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleComplete}
          disabled={isSubmitting}
          sx={{ minWidth: 120 }}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
        >
          {isSubmitting ? 'Loading...' : 'Go to Dashboard'}
        </Button>
      </Box>
    </Box>
  );
};
